import { AxiosError, AxiosResponse } from "axios";
import { DataType, FieldType } from "@websential/cosmic-v20";
import { IGetListRequest, IResponse, IServerError } from "../models";
import { format422Errors, useNavigateToLogout } from "../common/Common";

import { GridSize } from "@mui/material";
import { IClientListPayload } from "../models/client";
import { IDropDownListResponse } from "../models/common/IDropDownListResponse";
import { INITIAL_PAGE_SIZE } from "../common/constants";
import http from "../common/http-commons";

export const InvoiceService = {
  getInvoices: async (
    data: IGetListRequest,
    isAdminUser: any,
  ): Promise<any | IServerError | IResponse | any[]> => {
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        "/v1/Invoice/?order_by=" +
          data.order_by +
          "&order_direction=" +
          data.order_direction +
          "&page=" +
          data.page +
          "&size=" +
          data.size +
          "&search_query=" +
          data.search_query,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return { status: false, message: errorMessage, error: true };
    }
  },

  deleteById: async (
    delPayload: any,
  ): Promise<string | IServerError | IResponse | null> => {
    if (!delPayload.invoiceId) return null;
    try {
      const response: AxiosResponse<string> = await http.delete<string>(
        `/v1/Invoice/${delPayload.invoiceId}/`,
      );

      if (response.status === 204) {
        return {
          status: true,
          message: "Accountant has been deleted successfully",
        };
      } else if (response.status === 200) {
        return {
          status: true,
          message: "Accountant has been deleted successfully",
        };
      } else if (response.status === 404) {
        return response.data;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        delPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  create: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    if (requestPayload.id) {
      try {
        const response: AxiosResponse<any> = await http.put<any>(
          requestPayload.superUser === 400
            ? `/v1/Invoice/super-user/${requestPayload.id}/`
            : `/v1/Invoice/${requestPayload.id}/`,
          requestPayload,
        );

        if (response.status === 200) {
          return response;
        } else if (response.status === 400) {
          return response.data;
        } else if (response.status === 404) {
          return response.data;
        } else if (response.status === 500) {
          return {
            status: false,
            message: "Something went wrong.",
            error: true,
          };
        }
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error("An error occurred:", axiosError);
        const errorMessage =
          axiosError.response && typeof axiosError.response.data === "string"
            ? axiosError.response.data
            : "Unknown error";
        if (axiosError && axiosError.status === 401) {
          requestPayload.navigateToLogout("/logout");
          return {
            status: false,
            message: "UnAuthrized",
          };
        } else if (axiosError && axiosError.status === 422) {
          return {
            status: false,
            message: `Errors : \n${format422Errors(
              axiosError?.response?.data,
            )}`,
          };
        } else {
          return {
            status: false,
            message: errorMessage,
          };
        }
      }
    }

    try {
      const response: AxiosResponse<any> = await http.post<any>(
        requestPayload.superUser === 400
          ? "/v1/Invoice/super-user"
          : "/v1/Invoice/",
        requestPayload,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 404) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  getDDL: async (
    clientId: string,
  ): Promise<IDropDownListResponse[] | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<IDropDownListResponse[]> = await http.get<
        IDropDownListResponse[]
        // >("/v1/Invoice/ddl");
      >(`/v1/billing-calculation/client-wise/?client_id=${clientId}`);

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch {}
  },
};
